import { useState } from 'react'
import { Badge, Card, DataState, PageHeader } from '@/components/Common'
import { CheckField, Field, FormSection, TextArea, TextField } from '@/components/Form'
import { ConfirmDialog, Modal } from '@/components/Modal'
import { useAuth } from '@/lib/auth'
import { useFetch } from '@/lib/hooks'
import { useResource } from '@/lib/resource'
import type { Device, FacilityTree, UnregisteredCaller } from '@/lib/api'

const EMPTY = {
  name: '',
  ae_title: '',
  hostname: '',
  port: 104,
  source_ip: '',
  device_type: '',
  modality: '',
  station_name: '',
  location: '',
  facility_id: null as number | null,
  enabled: true,
  allow_echo: true,
  allow_worklist: true,
  notes: '',
  department_ids: [] as number[],
  primary_department_id: null as number | null,
}

type Draft = typeof EMPTY

/**
 * The modalities that may ask us for a worklist.
 *
 * Identity is the PAIR (AE title, source address), not the title alone: two
 * hospitals legitimately have a scanner called CT1, and this page has to be
 * able to express that rather than making somebody rename one.
 *
 * The column that matters most is the facility. A device without one is served
 * NOTHING by any tenancy-scoped rule — the fail-closed direction — and that is
 * invisible from the modality, so it is flagged here rather than left to be
 * discovered at 7am.
 */
export function Devices() {
  const devices = useResource<Device>('/api/v1/devices')
  const tenancy = useFetch<FacilityTree[]>('/api/v1/tenancy')
  const unknown = useFetch<UnregisteredCaller[]>('/api/v1/query-log/unregistered?hours=168')
  const { can } = useAuth()

  const [editing, setEditing] = useState<Device | 'new' | null>(null)
  const [prefill, setPrefill] = useState<Partial<Draft> | null>(null)
  const [deleting, setDeleting] = useState<Device | null>(null)

  const mayEdit = can('config_admin')
  const list = devices.data ?? []
  const multiSite = (tenancy.data ?? []).length > 1
  const unassigned = list.filter((d) => d.enabled && !d.facility_id)

  return (
    <>
      <PageHeader
        title="Devices"
        subtitle="Which modalities may ask for a worklist, and where each one is."
        actions={
          mayEdit && (
            <button
              className="btn btn-primary"
              onClick={() => {
                setPrefill(null)
                setEditing('new')
              }}
            >
              Add device
            </button>
          )
        }
      />

      {devices.saveError && <div className="notice notice-error">{devices.saveError}</div>}

      {multiSite && unassigned.length > 0 && (
        <div className="notice notice-error">
          <strong>
            {unassigned.length} enabled device{unassigned.length === 1 ? ' has' : 's have'} no
            facility.
          </strong>{' '}
          Any rule that scopes to the calling device's facility matches nothing
          for them, so they receive an empty worklist with no error at the
          modality. This is the fail-closed direction and it is deliberate —
          across sites, a too-wide list is a privacy incident.
        </div>
      )}

      {(unknown.data ?? []).length > 0 && (
        <Card title="Seen but not registered">
          <p className="card-note">
            These have asked for a worklist in the last week and are not in the
            list below. Usually a device somebody plugged in without telling
            anyone — take the AE title and address from here rather than from
            the commissioning sheet, because this is what it actually sends.
          </p>
          <table className="ae-table">
            <thead>
              <tr>
                <th>AE title</th>
                <th>From</th>
                <th>Port</th>
                <th>Queries</th>
                <th>Last seen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(unknown.data ?? []).map((caller, i) => (
                <tr key={i}>
                  <td className="mono">{caller.calling_ae}</td>
                  <td className="mono">{caller.source_ip}</td>
                  <td className="num">{caller.listener_port}</td>
                  <td className="num">{caller.queries}</td>
                  <td className="hint">{caller.last_seen}</td>
                  <td className="actions">
                    {mayEdit && (
                      <button
                        className="btn btn-small"
                        onClick={() => {
                          setPrefill({
                            name: caller.calling_ae,
                            ae_title: caller.calling_ae,
                            source_ip: caller.source_ip,
                          })
                          setEditing('new')
                        }}
                      >
                        Register
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card>
        <DataState
          loading={devices.loading}
          error={devices.error}
          empty={list.length === 0}
          emptyMessage="No devices registered."
        />

        {list.length > 0 && (
          <div className="table-wrap">
            <table className="ae-table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>AE title</th>
                  <th>Source address</th>
                  <th>Facility</th>
                  <th>Departments</th>
                  <th>Tokens</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((device) => (
                  <tr key={device.id} className={device.enabled ? undefined : 'row-muted'}>
                    <td>
                      <strong>{device.name}</strong>
                      {!device.enabled && <> <Badge state="DISABLED" /></>}
                      {device.enabled && !device.allow_worklist && (
                        <div className="warn-text">worklist queries not permitted</div>
                      )}
                    </td>
                    <td className="mono">{device.ae_title}</td>
                    <td className="mono">
                      {device.source_ip || (
                        <span className="hint">any address (wildcard)</span>
                      )}
                    </td>
                    <td>
                      {device.facility_code || (
                        <span className={multiSite ? 'warn-text' : 'hint'}>none</span>
                      )}
                    </td>
                    <td className="hint">
                      {device.departments.length === 0
                        ? '—'
                        : device.departments
                            .map((d) => (d.is_primary ? `${d.code}*` : d.code))
                            .join(', ')}
                    </td>
                    <td className="hint">
                      {[device.modality, device.station_name, device.location]
                        .filter(Boolean)
                        .join(' · ') || <span className="warn-text">none set</span>}
                    </td>
                    <td className="actions">
                      {mayEdit && (
                        <>
                          <button className="btn btn-small" onClick={() => setEditing(device)}>
                            Edit
                          </button>
                          <button
                            className="btn btn-small btn-danger-quiet"
                            onClick={() => setDeleting(device)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="table-footnote">
          A <code>*</code> marks the primary department, which is what{' '}
          <code>$AE_DEPARTMENT</code> resolves to. The Tokens column is what{' '}
          <code>$AE_MODALITY</code>, <code>$AE_STATION</code> and{' '}
          <code>$AE_LOCATION</code> resolve to — a blank one makes any filter
          using it inert, so the device sees a <em>wider</em> list than intended.
        </p>
      </Card>

      {editing && (
        <DeviceEditor
          device={editing === 'new' ? null : editing}
          prefill={prefill}
          tenancy={tenancy.data ?? []}
          saving={devices.saving}
          error={devices.saveError}
          onClose={() => {
            setEditing(null)
            setPrefill(null)
            devices.clearError()
          }}
          onSave={async (draft) => {
            const saved =
              editing === 'new'
                ? await devices.create(draft)
                : await devices.update(editing.id, draft)
            if (saved) {
              setEditing(null)
              setPrefill(null)
              unknown.reload()
            }
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Delete '${deleting.name}'?`}
          confirmLabel="Delete device"
          destructive
          busy={devices.saving}
          error={devices.saveError}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            if (await devices.remove(deleting.id)) setDeleting(null)
          }}
        >
          <p>
            <strong>Disabling is almost always better.</strong> A deleted device
            falls through to whatever the catch-all rule does, which is not a
            diagnosable state. A disabled one is refused with a message that
            names it.
          </p>
          <p className="hint">
            Deleting also loses the commissioning notes, and the query log rows
            for this device stop being explicable.
          </p>
        </ConfirmDialog>
      )}
    </>
  )
}

function DeviceEditor({
  device,
  prefill,
  tenancy,
  saving,
  error,
  onClose,
  onSave,
}: {
  device: Device | null
  prefill: Partial<Draft> | null
  tenancy: FacilityTree[]
  saving: boolean
  error: string | null
  onClose: () => void
  onSave: (draft: Draft) => void
}) {
  const [draft, setDraft] = useState<Draft>(() =>
    device
      ? {
          name: device.name,
          ae_title: device.ae_title,
          hostname: device.hostname,
          port: device.port,
          source_ip: device.source_ip,
          device_type: device.device_type,
          modality: device.modality,
          station_name: device.station_name,
          location: device.location,
          facility_id: device.facility_id,
          enabled: device.enabled,
          allow_echo: device.allow_echo,
          allow_worklist: device.allow_worklist,
          notes: device.notes,
          department_ids: device.department_ids,
          primary_department_id: device.primary_department_id,
        }
      : { ...EMPTY, ...prefill },
  )

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  const departments = tenancy.find((f) => f.id === draft.facility_id)?.departments ?? []
  const multiSite = tenancy.length > 1

  const toggleDepartment = (id: number, on: boolean) => {
    setDraft((d) => {
      const next = on
        ? [...new Set([...d.department_ids, id])]
        : d.department_ids.filter((x) => x !== id)
      return {
        ...d,
        department_ids: next,
        // The primary has to be one of the members; dropping it clears it
        // rather than leaving a primary the device does not serve.
        primary_department_id: next.includes(d.primary_department_id ?? -1)
          ? d.primary_department_id
          : null,
      }
    })
  }

  return (
    <Modal
      title={device ? `Edit '${device.name}'` : 'Add device'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={saving || !draft.name || !draft.ae_title}
            onClick={() => onSave(draft)}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <div className="notice notice-error">{error}</div>}

      <FormSection
        title="Identity"
        tone="inbound"
        description="The AE title and source address together are how the daemon recognises this device. Both are matched; neither is enough alone."
      >
        <div className="form-grid">
          <TextField label="Name" value={draft.name} onChange={(v) => set('name', v)} required />
          <TextField
            label="AE title"
            value={draft.ae_title}
            onChange={(v) => set('ae_title', v)}
            maxLength={16}
            required
            showCount
            hint="What it calls itself on the wire. Take it from the query log, not the commissioning sheet — they differ more often than not."
          />
          <TextField
            label="Source address"
            value={draft.source_ip}
            onChange={(v) => set('source_ip', v)}
            maxLength={45}
            hint="Where inbound associations come from. Leave empty ONLY for a device that genuinely roams between VLANs — it weakens the pairing authorization rests on."
          />
          <TextField
            label="Device type"
            value={draft.device_type}
            onChange={(v) => set('device_type', v)}
            maxLength={32}
            hint="Free text, for the console. Never used to decide anything."
          />
        </div>
      </FormSection>

      <FormSection
        title="Where it is"
        description={
          multiSite
            ? 'Required in practice: a rule that scopes to the calling device’s facility matches NOTHING for a device without one.'
            : 'Optional while there is one facility. Fill it in before adding a second.'
        }
      >
        <div className="form-grid">
          <Field label="Facility">
            <select
              value={draft.facility_id ?? ''}
              onChange={(e) => {
                const value = e.target.value === '' ? null : Number(e.target.value)
                setDraft((d) => ({
                  ...d,
                  facility_id: value,
                  department_ids: [],
                  primary_department_id: null,
                }))
              }}
            >
              <option value="">not assigned</option>
              {tenancy.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.code} — {f.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {multiSite && !draft.facility_id && (
          <div className="notice notice-warn">
            Without a facility this device is invisible to every tenancy-scoped
            rule, and the symptom is a blank worklist with no error.
          </div>
        )}

        {draft.facility_id && (
          <>
            <div className="subhead">Departments it serves</div>
            <p className="hint">
              Several is normal — a portable covers the emergency department and
              the wards. The primary is what <code>$AE_DEPARTMENT</code>{' '}
              resolves to; leave it unset for a device that genuinely does not
              belong to one.
            </p>
            <div className="services">
              {departments.length === 0 && (
                <span className="hint">This facility has no departments yet.</span>
              )}
              {departments.map((d) => (
                <div key={d.id} className="perm">
                  <CheckField
                    label={`${d.code} — ${d.name}`}
                    checked={draft.department_ids.includes(d.id)}
                    onChange={(on) => toggleDepartment(d.id, on)}
                  />
                  {draft.department_ids.includes(d.id) && (
                    <label className="check">
                      <input
                        type="radio"
                        name="primary"
                        checked={draft.primary_department_id === d.id}
                        onChange={() => set('primary_department_id', d.id)}
                      />
                      <span className="check-detail">primary</span>
                    </label>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </FormSection>

      <FormSection
        title="Values rules can use"
        description={
          <>
            These feed the <code>$AE_MODALITY</code>, <code>$AE_STATION</code>{' '}
            and <code>$AE_LOCATION</code> tokens. Fill them in even if no rule
            uses them today — a blank one makes a filter using it{' '}
            <strong>inert</strong>, so the device sees a wider list than
            intended and the only sign is a log line.
          </>
        }
      >
        <div className="form-grid">
          <TextField
            label="Modality"
            value={draft.modality}
            onChange={(v) => set('modality', v)}
            maxLength={16}
            placeholder="CT"
            hint="$AE_MODALITY. Leave blank for a device that does several."
          />
          <TextField
            label="Station name"
            value={draft.station_name}
            onChange={(v) => set('station_name', v)}
            maxLength={64}
            hint="$AE_STATION. Often matched against Scheduled Station AE Title."
          />
          <TextField
            label="Location"
            value={draft.location}
            onChange={(v) => set('location', v)}
            maxLength={64}
            hint="$AE_LOCATION."
          />
        </div>
      </FormSection>

      <FormSection title="Permissions and outbound">
        <CheckField
          label="Enabled"
          detail="A disabled device is refused with a message naming it — which is a diagnosable state, unlike deleting it."
          checked={draft.enabled}
          onChange={(v) => set('enabled', v)}
        />
        <CheckField
          label="May run worklist queries"
          detail="ANDed with the listener's own accept_find and query policy. The narrowest wins."
          checked={draft.allow_worklist}
          onChange={(v) => set('allow_worklist', v)}
        />
        <CheckField
          label="May be sent a connectivity test"
          detail="Outbound C-ECHO from the console. Inbound C-ECHO is governed by the listener, not by this."
          checked={draft.allow_echo}
          onChange={(v) => set('allow_echo', v)}
        />
        <div className="form-grid">
          <TextField
            label="Hostname"
            value={draft.hostname}
            onChange={(v) => set('hostname', v)}
            maxLength={255}
            hint="Only for the outbound test. Not the same as the source address, and not interchangeable with it."
          />
          <Field label="Port">
            <input
              type="number"
              value={draft.port}
              onChange={(e) => set('port', Number(e.target.value))}
            />
          </Field>
        </div>
      </FormSection>

      <FormSection title="Notes">
        <TextArea
          label="Notes"
          value={draft.notes}
          onChange={(v) => set('notes', v)}
          maxLength={1000}
          hint="Commissioning date, who owns it, why the source address is blank if it is."
        />
      </FormSection>
    </Modal>
  )
}
